class PlanPickerMenobacs extends HTMLElement {
  connectedCallback() {
    this.classList.remove('hidden');
    this.variantSelects = this.closest('variant-selects');

    this.querySelectorAll('.menobacs-card').forEach((card) => {
      card.addEventListener('click', () => this.selectCard(card));
    });

    this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (data?.sectionId !== this.dataset.section) return;
      this.syncPurchaseTypeAfterVariantUpdate();
    });

    this.init();
  }

  disconnectedCallback() {
    this.onVariantChangeUnsubscriber?.();
  }

  init() {
    const params = new URLSearchParams(window.location.search);
    const variantParam = params.get('variant');

    if (variantParam) {
      const cardById =
        this.querySelector(`.menobacs-card[data-type="subscription"][data-variant-id="${variantParam}"]`) ||
        this.querySelector(`.menobacs-card[data-variant-id="${variantParam}"]`);
      if (cardById) {
        this.selectCard(cardById);
        return;
      }

      const matchingInfo = this.variantSelects?.querySelector(
        `.option__info[data-variant-id="${variantParam}"]`
      );
      if (matchingInfo) {
        const type = matchingInfo.dataset.type;
        const nameEl = matchingInfo.querySelector('.option__info-name span.h5');
        const optionName = nameEl?.textContent.trim();
        if (optionName && type) {
          const escapedName = optionName.replace(/"/g, '\\"');
          const card = this.querySelector(
            `.menobacs-card[data-type="${type}"][data-value="${escapedName}"]`
          );
          if (card) {
            this.selectCard(card);
            return;
          }
        }
      }

      return;
    }

    if (window.isGoogleAdsCampaignTraffic?.()) {
      const firstCard = this.querySelector('.menobacs-card');
      if (firstCard) this.selectCard(firstCard);
      return;
    }

    if (this.isSimplifiedV1 && this.variantSelects?.dataset.currentSelect?.trim() === '0') {
      const onetimerCard = this.querySelector('.menobacs-card[data-type="onetimer"]');
      if (onetimerCard) {
        this.selectCard(onetimerCard);
        return;
      }
    }

    const currentSelect = parseInt(this.variantSelects?.dataset.currentSelect || '0', 10);
    if (currentSelect > 0) {
      const packageFieldset = this.variantSelects?.querySelector('.product-options_package');
      const checkedInput = packageFieldset?.querySelector('input[type="radio"]:checked');
      const label = checkedInput ? packageFieldset?.querySelector(`label[for="${checkedInput.id}"]`) : null;
      const optionInfo = label?.querySelector('.option__info:not(.hidden)');

      if (optionInfo) {
        const type = optionInfo.dataset.type;
        const nameEl = optionInfo.querySelector('.option__info-name span.h5');
        const optionName = nameEl?.textContent.trim();

        if (optionName && type) {
          const escapedName = optionName.replace(/"/g, '\\"');
          const card = this.querySelector(
            `.menobacs-card[data-type="${type}"][data-value="${escapedName}"]`
          );
          if (card) {
            this.selectCard(card);
            return;
          }
        }
      }
    }

    const firstCard = this.querySelector('.menobacs-card');
    if (firstCard) this.selectCard(firstCard);
  }

  selectCard(card) {
    if (!card || !this.variantSelects) return;

    const type = card.dataset.type;

    this.querySelectorAll('.menobacs-card').forEach((c) => c.classList.remove('active'));
    card.classList.add('active');
    this.dataset.currentType = type;

    this.variantSelects.lockSubmitUntilVariantSettled?.();
    this.variantSelects.updatePurchaseTypeInput?.(type);
    this.variantSelects.toggleProductFormInput?.(type);

    this.variantSelects.querySelectorAll('.option__info').forEach((info) => {
      info.classList.toggle('hidden', info.dataset.type !== type);
    });

    this.variantSelects.querySelectorAll('.variant-option_package').forEach((option) => {
      const hasType = option.querySelectorAll(`[data-type="${type}"]`).length > 0;
      option.classList.toggle('hidden', !hasType);
    });

    // Write id / selling_plan into the form immediately. The section refetch
    // that follows can take several seconds on this PDP; ATC must not wait for it.
    this.syncFormToCard(card);
    this.triggerRadioForCard(card);
    this.syncFormToCard(card);

    const giftOptionWrapper = this.variantSelects.querySelector('.option__info-gifts-wrapper');
    if (giftOptionWrapper) {
      giftOptionWrapper.classList.toggle('hidden', type === 'onetimer');
    }

    this.syncPurchaseTypeAfterVariantUpdate();
    this.unlockAddToCart();
  }

  syncPurchaseTypeAfterVariantUpdate() {
    const type = this.dataset.currentType;
    if (!type || !this.variantSelects) return;

    this.variantSelects.updatePurchaseTypeInput?.(type);
    this.variantSelects.toggleProductFormInput?.(type);

    const sectionId = this.dataset.section;
    const productForm =
      document.querySelector(`product-form[data-section-id="${sectionId}"]`) || this.getProductForm();
    productForm?.togglePrices?.();

    // togglePrices() is patched below to re-apply the strike price, but call it
    // directly as well in case product-form is not upgraded yet.
    this.applyAtcStrike(productForm);
  }

  get isSimplifiedV1() {
    return this.classList.contains('plan-picker-simplified-v1');
  }

  /**
   * Simplified selector v1: mirror the active card's strikethrough price in the
   * add-to-cart button (.menobacs-atc-strike slot from product-form-price.liquid).
   * The generic influencer logic in product-form.js#togglePrices picks the strike
   * price by radio index, which does not match the card order of this selector,
   * so its inline strike is replaced here.
   */
  applyAtcStrike(productForm) {
    if (!this.isSimplifiedV1 || !productForm) return;

    const submitButton = productForm.querySelector('[type="submit"]');
    if (!submitButton) return;

    // Configured influencer strike (visible on the card) wins; otherwise fall back
    // to the real regular price of the package (data-atc-strike-price).
    const card = this.querySelector('.menobacs-card.active');
    const hasVisibleStrike = !!card?.querySelector('.menobacs-card__strike');
    const strikePrice = (
      (hasVisibleStrike ? card.dataset.strikePrice : '') ||
      card?.dataset.atcStrikePrice ||
      ''
    ).trim();

    const mainPrice = submitButton.querySelector('.atc-price_main');
    mainPrice?.querySelectorAll('span[style*="line-through"]').forEach((el) => el.remove());

    const strikeEl = submitButton.querySelector('.menobacs-atc-strike');
    if (strikeEl) {
      strikeEl.textContent = strikePrice;
      strikeEl.hidden = !strikePrice;
      strikeEl.style.display = strikePrice ? '' : 'none';
    }

    // Avoid a double strike when the variant also has a compare-at price.
    submitButton
      .querySelectorAll('.selected-variant-price .atc-price_sale:not(.menobacs-atc-strike)')
      .forEach((el) => {
        el.style.display = strikePrice ? 'none' : '';
      });

    const v1Price = document.querySelector('.selected-variant-price-v1 .atc-price_main-v1');
    if (v1Price) {
      v1Price
        .querySelectorAll('span[style*="line-through"], .simplified-v1-atc-strike')
        .forEach((el) => el.remove());

      if (strikePrice) {
        const strikeSpan = document.createElement('span');
        strikeSpan.className = 'simplified-v1-atc-strike fw-400';
        strikeSpan.style.cssText =
          'color: #000413; text-decoration: line-through; margin-right: 8px; font-size: 90%; opacity: 0.5;';
        strikeSpan.textContent = strikePrice;
        v1Price.prepend(strikeSpan);
      }
    }
  }

  getProductForm() {
    const sectionId = this.dataset.section;
    return (
      this.closest('product-info')?.querySelector(`product-form[data-section-id="${sectionId}"]`) ||
      this.closest('product-info')?.querySelector('product-form') ||
      document.querySelector(`product-form[data-section-id="${sectionId}"]`) ||
      document.querySelector('product-form')
    );
  }

  getProductFormElement() {
    const productForm = this.getProductForm();
    return (
      productForm?.querySelector('form') ||
      document.getElementById(`product-form-${this.dataset.section}`)
    );
  }

  syncFormToCard(card) {
    const form = this.getProductFormElement();
    if (!form) return;

    const variantId = card.dataset.variantId;
    const type = card.dataset.type;
    const sellingPlanId = card.dataset.sellingPlanId;

    form.querySelectorAll('input[name="id"]').forEach((input) => {
      if (variantId) input.value = variantId;
    });

    const sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    if (sellingPlanInput) {
      if (type === 'onetimer') {
        sellingPlanInput.setAttribute('disabled', 'disabled');
      } else {
        sellingPlanInput.removeAttribute('disabled');
        if (sellingPlanId) sellingPlanInput.value = sellingPlanId;
      }
    }

    this.getProductForm()?.separateProductsLogic?.();
  }

  unlockAddToCart() {
    const productForm = this.getProductForm();
    if (!productForm) return;

    if (typeof productForm.unlockForVariantChangeIfIdle === 'function') {
      productForm.unlockForVariantChangeIfIdle();
    } else {
      productForm.toggleSubmitButton?.(false);
    }
  }

  getRadioForCard(card) {
    if (!this.variantSelects) return null;

    const type = card.dataset.type;
    const variantId = card.dataset.variantId;
    const optionName = card.dataset.value;
    let optionInfo = null;

    if (variantId) {
      optionInfo = this.variantSelects.querySelector(
        `.option__info[data-type="${type}"][data-variant-id="${variantId}"]`
      );
    }

    if (!optionInfo && optionName) {
      const optionInfos = this.variantSelects.querySelectorAll(`.option__info[data-type="${type}"]`);
      for (const info of optionInfos) {
        const nameEl = info.querySelector('.option__info-name span.h5');
        if (nameEl?.textContent.trim() === optionName) {
          optionInfo = info;
          break;
        }
      }
    }

    const label = optionInfo?.closest('label, .variant-option_package');
    const inputId = label?.getAttribute('for');
    return inputId ? document.getElementById(inputId) : null;
  }

  triggerRadioForCard(card) {
    const input = this.getRadioForCard(card);
    if (!input) return;

    const alreadyChecked = input.checked;
    input.checked = true;
    if (!alreadyChecked) {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

customElements.define('plan-picker-menobacs', PlanPickerMenobacs);

// Simplified selector v1: the ATC button is re-rendered after every variant
// refetch and after add-to-cart, and each time product-form#togglePrices runs.
// Re-apply the card strike price after every togglePrices call so it survives
// those re-renders. Only acts when a simplified-v1 picker is on the page.
customElements.whenDefined('product-form').then(() => {
  const proto = customElements.get('product-form')?.prototype;
  if (!proto || proto.__simplifiedV1StrikePatched || typeof proto.togglePrices !== 'function') return;

  const originalTogglePrices = proto.togglePrices;
  proto.togglePrices = function (...args) {
    const result = originalTogglePrices.apply(this, args);

    try {
      const sectionId = this.dataset.sectionId;
      const scope = this.closest('product-info') || document;
      const picker =
        scope.querySelector(`plan-picker-menobacs.plan-picker-simplified-v1[data-section="${sectionId}"]`) ||
        scope.querySelector('plan-picker-menobacs.plan-picker-simplified-v1');
      picker?.applyAtcStrike?.(this);
    } catch (error) {
      console.error(error);
    }

    return result;
  };

  proto.__simplifiedV1StrikePatched = true;
});
